import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import styles from './Modal.module.css';
import { updateCase } from '../../../services/clienteService';
import { ClienteCaso, NewCaseFormData } from '../../../interfaces/cliente.types';
interface EditCasoModalProps {
    onClose: () => void;
    onCaseUpdated: (updatedCase: ClienteCaso) => void;
    caseToEdit: ClienteCaso;
}

export const EditCasoModal: React.FC<EditCasoModalProps> = ({ onClose, onCaseUpdated, caseToEdit }) => {
    const [formData, setFormData] = useState<Partial<NewCaseFormData>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (caseToEdit) {
            setFormData({
                nombre_completo: caseToEdit.nombre_completo,
                tipo_caso: caseToEdit.tipo_caso,
                numero_expediente: caseToEdit.numero_expediente || '',
                dni: caseToEdit.dni || '',
                telefono: caseToEdit.telefono || '',
                email: caseToEdit.email || '',
                direccion: caseToEdit.direccion || '',
                monto_total_acordado: caseToEdit.monto_total_acordado || 0,
                lugar: caseToEdit.lugar || '',
                juzgado: caseToEdit.juzgado || '',
                descripcion_caso: caseToEdit.descripcion_caso || ''
            });
        }
    }, [caseToEdit]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.nombre_completo || !formData.tipo_caso) {
            setError('El Nombre del Cliente y el Tipo de Caso son obligatorios.');
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const updatedCase = await updateCase(caseToEdit.id, formData);
            onCaseUpdated(updatedCase);
            onClose();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const modalVariants = {
        hidden: { y: 50, opacity: 0 },
        visible: { y: 0, opacity: 1 },
        exit: { y: 50, opacity: 0 }
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <motion.div 
                className={styles.modalContent}
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                onClick={(e) => e.stopPropagation()}
            >
                <header className={styles.modalHeader}>
                    <h2>Editar Expediente</h2>
                    <button onClick={onClose} className={styles.closeButton}><X size={24} /></button>
                </header>
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGrid}>
                        <div className={styles.formGroup}>
                            <label htmlFor="edit_nombre_completo">Nombre Completo del Cliente*</label>
                            <input type="text" id="edit_nombre_completo" name="nombre_completo" value={formData.nombre_completo || ''} onChange={handleChange} required />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="edit_tipo_caso">Tipo de Caso*</label>
                            <input type="text" id="edit_tipo_caso" name="tipo_caso" value={formData.tipo_caso || ''} onChange={handleChange} required />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="edit_numero_expediente">N° de Expediente</label>
                            <input type="text" id="edit_numero_expediente" name="numero_expediente" value={formData.numero_expediente || ''} onChange={handleChange} />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="edit_dni">DNI</label>
                            <input type="text" id="edit_dni" name="dni" value={formData.dni || ''} onChange={handleChange} />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="edit_telefono">Teléfono</label>
                            <input type="text" id="edit_telefono" name="telefono" value={formData.telefono || ''} onChange={handleChange} />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="edit_email">Email</label>
                            <input type="email" id="edit_email" name="email" value={formData.email || ''} onChange={handleChange} />
                        </div>
                         <div className={styles.formGroup}>
                            <label htmlFor="edit_juzgado">Juzgado</label>
                            <input type="text" id="edit_juzgado" name="juzgado" value={formData.juzgado || ''} onChange={handleChange} />
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="edit_lugar">Lugar / Sede</label>
                            <input type="text" id="edit_lugar" name="lugar" value={formData.lugar || ''} onChange={handleChange} />
                        </div>
                         <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <label htmlFor="edit_direccion">Dirección</label>
                            <input type="text" id="edit_direccion" name="direccion" value={formData.direccion || ''} onChange={handleChange} />
                        </div>
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                             <label htmlFor="edit_monto_total_acordado">Monto Acordado (S/)</label>
                            <input type="number" step="0.01" id="edit_monto_total_acordado" name="monto_total_acordado" value={formData.monto_total_acordado || ''} onChange={handleChange} placeholder="0.00" />
                        </div>
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <label htmlFor="edit_descripcion_caso">Descripción del Caso</label>
                            <textarea id="edit_descripcion_caso" name="descripcion_caso" rows={4} value={formData.descripcion_caso || ''} onChange={handleChange}></textarea>
                        </div>
                    </div>
                    {error && <p className={styles.errorMessage}>{error}</p>}
                    <footer className={styles.modalFooter}>
                        <button type="button" className={styles.cancelButton} onClick={onClose} disabled={isLoading}>Cancelar</button>
                        <button type="submit" className={styles.submitButton} disabled={isLoading}>
                            {isLoading ? 'Actualizando...' : 'Guardar Cambios'}
                        </button>
                    </footer>
                </form>
            </motion.div>
        </div>
    );
};